import { Component, PLATFORM_ID, Inject } from '@angular/core';
import { DOCUMENT, isPlatformBrowser } from '@angular/common';
import {
	SupabaseService
} from '@app/core/services';
// ** service
import { LayoutService } from '@app/core/services/layout.service';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
// ** rxjs
import { finalize, map, switchMap, tap, catchError } from 'rxjs/operators';
import { of, Observable, from, EMPTY } from 'rxjs';
import * as moment from 'moment';
import { MessageService } from '@app/core/services';
import { Router } from '@angular/router';

@Component({
	selector: 'app-my-account',
	templateUrl: './my-account.component.html',
	styleUrls: ['./my-account.component.scss']
})
export class MyAccountComponent {
	isBrowser: boolean;

	discountCodes = [];

	buyerForm: FormGroup = this.formBuilder.group({
		buyerName: ['', Validators.required],
		buyerNickName: [''],
		buyerPhone: ['', Validators.required],
		buyerBankAccount: ['', Validators.required],
		'711Name': ['', Validators.required]
	});

	constructor(
		@Inject(DOCUMENT) private document: Document,
		@Inject(PLATFORM_ID) private platformId,
		private supabaseService: SupabaseService,
		private formBuilder: FormBuilder,
		public layoutService: LayoutService,
		public messageService: MessageService,
		private router: Router
	) {
		this.isBrowser = isPlatformBrowser(this.platformId);
	}

	ngOnInit(): void {
		const canonical = this.document.querySelector('link[rel="canonical"]') as HTMLAnchorElement | null;
		if (canonical) canonical.href = 'https://bigaunt666.github.io/bigaunt/';
		of(this.layoutService.setPageLoading(true))
			.pipe(
				switchMap(() => this.getSession$()),
				switchMap((sessionRes) => {
					if (!sessionRes.session) {
						this.router.navigate(['/signIn']);
						return of(null);
					}
					this.setUserData();
					return this.getDiscountCode$(this.getProfileFromStorage()?.id);
				}),
				tap((discountCode) => {
					if (discountCode) this.discountCodes = discountCode;
				}),
				finalize(() => this.layoutService.setPageLoading(false))
			)
			.subscribe();
	}

	// 帶入會員資料
	setUserData() {
		const profile = this.getProfileFromStorage();
		if (!profile) return;
		const userData = {
			buyerName: profile.name,
			buyerNickName: profile.nickName,
			buyerPhone: profile.phone,
			buyerBankAccount: profile.bankAccount,
			'711Name': profile['711Name']
		};
		this.buyerForm.patchValue(userData);
	}

	// - 從 localStorage 取得個人資料 (SSR/prerender 環境沒有 localStorage，回傳 null)
	getProfileFromStorage(): any {
		if (!this.isBrowser) return null;
		const raw = localStorage.getItem('profile');
		return raw ? JSON.parse(raw) : null;
	}

	// - 寫入個人資料到 localStorage (SSR/prerender 環境沒有 localStorage，跳過)
	setProfileToStorage(profile: any): void {
		if (!this.isBrowser) return;
		localStorage.setItem('profile', JSON.stringify(profile));
	}

	updateProfile() {
		// 檢查必填欄位及格式
		if (this.buyerForm.invalid) {
			this.messageService.add({
				severity: 'error',
				summary: `注意`,
				detail: '尚有必填欄位未填寫或是格式不對',
				icon: 'bounce animated',
				life: 5000,
			});
			return;
		}
		const params = {
			name: this.buyerForm.value.buyerName,
			nickName: this.buyerForm.value.buyerNickName,
			phone: this.buyerForm.value.buyerPhone,
			bankAccount: this.buyerForm.value.buyerBankAccount,
			'711Name': this.buyerForm.value['711Name']
		}
		const profile = this.getProfileFromStorage();
		of(this.layoutService.setPageLoading(true))
			.pipe(
				switchMap(() => this.updateProfile$(profile?.id, params)),
				switchMap((res) => this.getProfile$(res[0].id)),
				tap((res) => {
					this.setProfileToStorage(res[0]);
					this.messageService.add({
						severity: 'success',
						summary: `成功`,
						detail: '更新成功',
						icon: 'bounce animated',
						life: 5000,
					});
				}),
				tap((res) => this.setUserData()),
				finalize(() => this.layoutService.setPageLoading(false))
			)
			.subscribe();
	}

	// ----------------------------------API--------------------------------------------------
	// - 取得登入狀態
	getSession$(): Observable<any> {
		return from(this.supabaseService.getSession()).pipe(
			map(({ data, error }) => {
				if (error) throw error;
				return data;
			})
		);
	}

	// - 取得個人資料
	getProfile$(userInfo: any): Observable<any> {
		return from(this.supabaseService.getProfile(userInfo)).pipe(
			map(({ data, error }) => {
				if (error) throw error;
				return data;
			})
		);
	}

	// - 取得個人折扣代碼
	getDiscountCode$(personId: any): Observable<any> {
		return from(this.supabaseService.getDiscountCode(personId)).pipe(
			map(({ data, error }) => {
				if (error) throw error;
				return data;
			})
		);
	}

	// - 更新個人資料
	updateProfile$(id, params): Observable<any> {
		return from(this.supabaseService.updateProfile(id, params)).pipe(
			map(({ data, error }) => {
				if (error) throw error;
				return data;
			})
		);
	}
}
